/**
 * CRA3D AI Training — single-lesson renderer.
 *
 * Supports two schemas:
 *   1. Rich curriculum lesson (Section C) — numbered_steps,
 *      worked_example, why_this_works, success_check, troubleshooting,
 *      accessible_alternative, assets (practice files / supplied assets
 *      / pending printables). Mark's verbatim step wording lives on
 *      step.body_verbatim and is rendered unchanged in a dedicated
 *      callout.
 *   2. Legacy sample / placeholder — top-level `sections[]` and
 *      `message` (status "in_production"). Kept so the one legacy
 *      sample id and three level placeholders still render honestly.
 *
 * The learner clicks "Mark success check as done" to record a
 * single `training_progress` row. No streaks are tracked.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { extractApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Clock, FileText, CheckCircle2, Quote, Download,
  Wrench, Ear, Lightbulb, Target, ArrowRight, Copy, Check,
  BookOpen, HelpCircle, Save,
} from "lucide-react";


export default function TrainingLesson() {
  const { lessonId } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ lesson: null, placeholder: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgradeNeeded, setUpgradeNeeded] = useState(false);
  const [markingDone, setMarkingDone] = useState(false);
  const [justCompleted, setJustCompleted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/training/lessons/${lessonId}`);
        setState({
          lesson:      r.data?.lesson || null,
          placeholder: !!r.data?.placeholder,
        });
      } catch (err) {
        if (err?.response?.status === 402) {
          setUpgradeNeeded(true);
          setError(extractApiError(
            err,
            "This lesson is included with the AI Training packages and memberships.",
          ));
        } else {
          setError(extractApiError(
            err,
            "Couldn't load this lesson right now.",
          ));
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [lessonId]);

  async function markDone() {
    setMarkingDone(true);
    try {
      await api.post("/training/progress", {
        lesson_id: lessonId,
        kind:      "lesson",
      });
      setJustCompleted(true);
      setState((s) => ({ ...s, lesson: { ...(s.lesson || {}), completed: true } }));
    } catch (err) {
      setError(extractApiError(err, "Couldn't record completion right now."));
    } finally {
      setMarkingDone(false);
    }
  }

  const lesson = state.lesson;
  const isRich = lesson && Array.isArray(lesson.numbered_steps);
  const nextLessonId = lesson?.success_check?.next_step;

  return (
    <AppShell>
      <div
        data-testid="training-lesson-page"
        className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 py-8 sm:py-12"
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => nav("/training")}
          data-testid="training-lesson-back-btn"
          className="mb-4 text-fg-secondary hover:text-fg-primary hover:bg-bg-surface-hover"
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          Back to AI Training
        </Button>

        {loading && (
          <p
            data-testid="training-lesson-loading"
            className="text-sm text-fg-secondary text-center py-12"
          >
            Loading lesson&hellip;
          </p>
        )}

        {!loading && upgradeNeeded && (
          <>
            <Alert
              variant="destructive"
              data-testid="training-lesson-paywall"
              className="mb-4"
            >
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <Link to="/plans">
              <Button
                data-testid="training-lesson-paywall-plans-btn"
                className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
              >
                See AI Training packages
              </Button>
            </Link>
          </>
        )}

        {!loading && !upgradeNeeded && error && (
          <Alert
            variant="destructive"
            data-testid="training-lesson-error"
            className="mb-4"
          >
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {!loading && !error && lesson && (
          <article data-testid="training-lesson-body">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge
                data-testid="training-lesson-body-label"
                className={
                  state.placeholder
                    ? "bg-transparent text-fg-muted hover:bg-transparent uppercase text-[10px] tracking-widest border border-fg-muted/40"
                    : lesson.level_id === "sample"
                      ? "bg-accent-warm text-bg-base hover:bg-accent-warm uppercase text-[10px] tracking-widest"
                      : "bg-bg-surface-hover text-fg-secondary hover:bg-bg-surface-hover uppercase text-[10px] tracking-widest border border-border-subtle"
                }
              >
                {lesson.label}
              </Badge>
              {state.placeholder && (
                <Badge
                  data-testid="training-lesson-body-status-badge"
                  className="bg-transparent text-fg-muted hover:bg-transparent uppercase text-[10px] tracking-widest border border-fg-muted/40"
                >
                  In production &mdash; not yet published
                </Badge>
              )}
              {(lesson.completed || justCompleted) && (
                <Badge
                  data-testid="training-lesson-body-completed-badge"
                  className="bg-transparent text-fg-primary hover:bg-transparent uppercase text-[10px] tracking-widest border border-accent-warm/60"
                >
                  Marked complete
                </Badge>
              )}
            </div>
            <h1
              data-testid="training-lesson-body-title"
              className="text-2xl sm:text-3xl font-heading font-medium text-fg-primary leading-tight"
            >
              {lesson.title}
            </h1>
            {lesson.outcome && (
              <p
                data-testid="training-lesson-body-outcome"
                className="mt-3 text-sm text-fg-secondary leading-relaxed"
              >
                <strong>Outcome.</strong> {lesson.outcome}
              </p>
            )}

            {state.placeholder ? (
              <PlaceholderBody lesson={lesson} />
            ) : isRich ? (
              <RichLessonBody
                lesson={lesson}
                justCompleted={justCompleted}
                markingDone={markingDone}
                onMarkDone={markDone}
                onOpenNext={nextLessonId ? () => nav(`/training/lesson/${nextLessonId}`) : null}
              />
            ) : (
              <LegacySectionsBody lesson={lesson} />
            )}

          </article>
        )}
      </div>
    </AppShell>
  );
}


function PlaceholderBody({ lesson }) {
  return (
    <div
      data-testid="training-lesson-body-placeholder"
      className="mt-6 rounded-lg border border-border-subtle bg-bg-surface p-6"
    >
      <div className="flex items-start gap-3">
        <Clock className="h-5 w-5 text-fg-secondary flex-shrink-0 mt-0.5" aria-hidden="true" />
        <p className="text-sm text-fg-primary leading-relaxed whitespace-pre-wrap">
          {lesson.message}
        </p>
      </div>
      <div className="mt-4">
        <Link to="/plans">
          <Button
            data-testid="training-lesson-body-plans-btn"
            size="sm"
            variant="outline"
            className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
          >
            See AI Training packages
          </Button>
        </Link>
      </div>
    </div>
  );
}


function LegacySectionsBody({ lesson }) {
  return (
    <div
      data-testid="training-lesson-body-sections"
      className="mt-6 space-y-6"
    >
      {(lesson.sections || []).map((sec, idx) => (
        <section
          key={idx}
          data-testid={`training-lesson-section-${idx}`}
          className="rounded-lg border border-border-subtle bg-bg-surface p-5"
        >
          <h2
            data-testid={`training-lesson-section-${idx}-heading`}
            className="text-base font-heading font-medium text-fg-primary"
          >
            {sec.heading}
          </h2>
          <p
            data-testid={`training-lesson-section-${idx}-body`}
            className="mt-2 text-sm text-fg-primary leading-relaxed whitespace-pre-wrap"
          >
            {sec.body}
          </p>
        </section>
      ))}
    </div>
  );
}


function RichLessonBody({ lesson, justCompleted, markingDone, onMarkDone, onOpenNext }) {
  const alreadyDone = !!lesson.completed || justCompleted;
  return (
    <div className="mt-6 space-y-6">
      {(lesson.before || lesson.review) && (
        <section
          data-testid="training-lesson-prereq"
          className="rounded-lg border border-border-subtle bg-bg-surface p-5 text-sm text-fg-primary"
        >
          {lesson.before && (
            <p data-testid="training-lesson-before">
              <strong>Before.</strong> {lesson.before}
            </p>
          )}
          {lesson.review && (
            <p className="mt-2" data-testid="training-lesson-review">
              <strong>Review.</strong> {lesson.review}
            </p>
          )}
        </section>
      )}

      <section
        data-testid="training-lesson-steps"
        className="rounded-lg border border-border-subtle bg-bg-surface p-5"
      >
        <h2 className="text-base font-heading font-medium text-fg-primary">
          Numbered steps
        </h2>
        <ol className="mt-3 space-y-4 list-decimal pl-5">
          {(lesson.numbered_steps || []).map((step, idx) => (
            <li
              key={idx}
              data-testid={`training-lesson-step-${idx}`}
              className="text-sm text-fg-primary leading-relaxed"
            >
              <p className="font-medium">{step.heading}</p>
              {step.body && (
                <p
                  data-testid={`training-lesson-step-${idx}-body`}
                  className="mt-1 whitespace-pre-wrap text-fg-primary"
                >
                  {step.body}
                </p>
              )}
              {step.body_verbatim && (
                <div
                  data-testid={`training-lesson-step-${idx}-verbatim`}
                  className="mt-2 rounded-md border border-accent-warm/40 bg-accent-warm/5 p-3 text-fg-primary"
                >
                  <div className="flex items-start gap-2">
                    <Quote className="h-3.5 w-3.5 mt-0.5 text-accent-warm flex-shrink-0" aria-hidden="true" />
                    <p className="text-sm leading-relaxed whitespace-pre-wrap font-serif">
                      {step.body_verbatim}
                    </p>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ol>
      </section>

      {lesson.worked_example && (
        <section
          data-testid="training-lesson-worked-example"
          className="rounded-lg border border-border-subtle bg-bg-surface p-5"
        >
          <div className="flex items-center gap-2 text-fg-primary">
            <Target className="h-4 w-4" aria-hidden="true" />
            <h2 className="text-base font-heading font-medium">Worked example</h2>
          </div>
          <p className="mt-2 text-sm text-fg-primary leading-relaxed whitespace-pre-wrap">
            {lesson.worked_example}
          </p>
        </section>
      )}

      {lesson.why_this_works && (
        <section
          data-testid="training-lesson-why"
          className="rounded-lg border border-border-subtle bg-bg-surface p-5"
        >
          <div className="flex items-center gap-2 text-fg-primary">
            <Lightbulb className="h-4 w-4" aria-hidden="true" />
            <h2 className="text-base font-heading font-medium">Why this works</h2>
          </div>
          <p className="mt-2 text-sm text-fg-primary leading-relaxed whitespace-pre-wrap">
            {lesson.why_this_works}
          </p>
        </section>
      )}

      {(lesson.guided_practice || lesson.independent_practice) && (
        <section
          data-testid="training-lesson-practice"
          className="rounded-lg border border-border-subtle bg-bg-surface p-5 text-sm text-fg-primary"
        >
          <h2 className="text-base font-heading font-medium">Practice</h2>
          {lesson.guided_practice && (
            <p className="mt-2" data-testid="training-lesson-guided-practice">
              <strong>Guided.</strong> {lesson.guided_practice}
            </p>
          )}
          {lesson.independent_practice && (
            <p className="mt-2" data-testid="training-lesson-independent-practice">
              <strong>Independent.</strong> {lesson.independent_practice}
            </p>
          )}
        </section>
      )}

      {lesson.learner_prompt && (
        <LearnerPromptBlock prompt={lesson.learner_prompt} />
      )}

      {Array.isArray(lesson.evidence_to_save) && lesson.evidence_to_save.length > 0 && (
        <section
          data-testid="training-lesson-evidence-to-save"
          className="rounded-lg border border-border-subtle bg-bg-surface p-5"
        >
          <div className="flex items-center gap-2 text-fg-primary">
            <FileText className="h-4 w-4" aria-hidden="true" />
            <h2 className="text-base font-heading font-medium">Evidence to save</h2>
          </div>
          <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-fg-primary">
            {lesson.evidence_to_save.map((e, i) => (
              <li
                key={i}
                data-testid={`training-lesson-evidence-${i}`}
              >
                {e}
              </li>
            ))}
          </ul>
        </section>
      )}

      {lesson.knowledge_check && (
        <KnowledgeCheckBlock
          lessonId={lesson.id}
          knowledgeCheck={lesson.knowledge_check}
        />
      )}

      {lesson.knowledge_check?.short_answer && (
        <ShortAnswerBlock
          lessonId={lesson.id}
          shortAnswer={lesson.knowledge_check.short_answer}
        />
      )}

      {(lesson.assets || []).length > 0 && (
        <section
          data-testid="training-lesson-assets"
          className="rounded-lg border border-border-subtle bg-bg-surface p-5"
        >
          <h2 className="text-base font-heading font-medium text-fg-primary">
            Supporting materials
          </h2>
          <ul className="mt-3 space-y-2">
            {lesson.assets.map((a, i) => (
              <li
                key={i}
                data-testid={`training-lesson-asset-${i}`}
                className="flex flex-wrap items-center gap-2 text-sm text-fg-primary"
              >
                {a.kind === "practice_file" && a.pf_id && (
                  <PracticeFileLink pfId={a.pf_id} label={a.label} />
                )}
                {a.kind === "printable" && (
                  <SuppliedAssetTile assetId={a.asset_id} label={a.label} pending={a.pending} />
                )}
                {a.kind !== "practice_file" && a.kind !== "printable" && (
                  <span>
                    <code className="text-xs font-mono text-fg-muted mr-2">{a.kind}</code>
                    {a.label}
                    {a.pending && (
                      <span className="ml-2 text-xs text-fg-muted">(asset pending)</span>
                    )}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {lesson.success_check && (
        <section
          data-testid="training-lesson-success-check"
          className="rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-5"
        >
          <div className="flex items-center gap-2 text-fg-primary">
            <CheckCircle2 className="h-4 w-4 text-accent-warm" aria-hidden="true" />
            <h2 className="text-base font-heading font-medium">Success check</h2>
          </div>
          {lesson.success_check.worked && (
            <p
              data-testid="training-lesson-success-check-worked"
              className="mt-2 text-sm text-fg-primary leading-relaxed"
            >
              <strong>I did it when:</strong> {lesson.success_check.worked}
            </p>
          )}
          {lesson.success_check.needs_attention && (
            <p
              data-testid="training-lesson-success-check-needs-attention"
              className="mt-2 text-sm text-fg-primary leading-relaxed"
            >
              <strong>Needs attention:</strong> {lesson.success_check.needs_attention}
            </p>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button
              data-testid="training-lesson-mark-done-btn"
              onClick={onMarkDone}
              disabled={markingDone || alreadyDone}
              className="bg-accent-warm text-bg-base hover:bg-accent-warm-hover"
            >
              {alreadyDone ? "Already marked complete" : markingDone ? "Saving\u2026" : "Mark success check as done"}
            </Button>
            {onOpenNext && (
              <Button
                data-testid="training-lesson-open-next-btn"
                size="sm"
                variant="outline"
                onClick={onOpenNext}
                className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
              >
                Next: {lesson.success_check.next_step}
                <ArrowRight className="h-3.5 w-3.5 ml-2" aria-hidden="true" />
              </Button>
            )}
          </div>
        </section>
      )}

      {(lesson.troubleshooting || []).length > 0 && (
        <section
          data-testid="training-lesson-troubleshooting"
          className="rounded-lg border border-border-subtle bg-bg-surface p-5"
        >
          <div className="flex items-center gap-2 text-fg-primary">
            <Wrench className="h-4 w-4" aria-hidden="true" />
            <h2 className="text-base font-heading font-medium">Troubleshooting</h2>
          </div>
          <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-fg-primary">
            {lesson.troubleshooting.map((t, i) => (
              <li key={i} data-testid={`training-lesson-troubleshooting-${i}`}>
                {t}
              </li>
            ))}
          </ul>
        </section>
      )}

      {lesson.accessible_alternative && (
        <section
          data-testid="training-lesson-accessible"
          className="rounded-lg border border-border-subtle bg-bg-surface p-5"
        >
          <div className="flex items-center gap-2 text-fg-primary">
            <Ear className="h-4 w-4" aria-hidden="true" />
            <h2 className="text-base font-heading font-medium">Accessible alternative</h2>
          </div>
          <p className="mt-2 text-sm text-fg-primary leading-relaxed">
            {lesson.accessible_alternative}
          </p>
        </section>
      )}
    </div>
  );
}


function PracticeFileLink({ pfId, label }) {
  const base = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const token = typeof window !== "undefined" ? window.localStorage?.getItem("auth_token") : null;
  const [downloading, setDownloading] = useState(false);
  async function onDownload() {
    setDownloading(true);
    try {
      const resp = await fetch(`${base}/training/practice-files/${pfId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pfId}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      /* noop; the lesson page remains stable */
    } finally {
      setDownloading(false);
    }
  }
  return (
    <>
      <code className="text-xs font-mono text-fg-muted">{pfId}</code>
      <span>{label}</span>
      <Button
        data-testid={`training-lesson-asset-download-${pfId}`}
        size="sm"
        variant="outline"
        onClick={onDownload}
        disabled={downloading}
        className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
      >
        <Download className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
        {downloading ? "Downloading\u2026" : "Download .txt"}
      </Button>
    </>
  );
}


function SuppliedAssetTile({ assetId, label, pending }) {
  // When the asset has been promoted to a real printable (SH9 /
  // Lesson 3.1 / Lesson 10.3), the lesson-level `pending` flag is
  // false AND the supplied-asset body carries a `printable_slug`.
  // The tile fetches the asset shape, then renders a direct download
  // button that hits /api/training/printables/{slug}.
  const base = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
  const token = typeof window !== "undefined"
    ? window.localStorage?.getItem("auth_token") : null;
  const [printableSlug, setPrintableSlug] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (pending) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/training/supplied-assets/${assetId}`);
        if (!cancelled) setPrintableSlug(r.data?.asset?.printable_slug || null);
      } catch (_e) {
        /* non-fatal — the tile just renders without the download. */
      }
    })();
    return () => { cancelled = true; };
  }, [assetId, pending]);

  async function onDownload() {
    if (!printableSlug) return;
    setDownloading(true);
    try {
      const resp = await fetch(`${base}/training/printables/${printableSlug}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `CRA3D_${printableSlug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_e) {
      /* stays on the page; the next click retries. */
    } finally {
      setDownloading(false);
    }
  }

  return (
    <span
      data-testid={`training-lesson-supplied-asset-${assetId}`}
      className={
        "inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs " +
        (pending
          ? "border-dashed border-fg-muted/40 text-fg-muted"
          : "border-border-subtle text-fg-primary")
      }
    >
      <code className="font-mono">{assetId}</code>
      <span>{label}</span>
      {pending && <span className="uppercase tracking-widest text-[10px]">asset pending</span>}
      {!pending && printableSlug && (
        <button
          type="button"
          data-testid={`training-lesson-supplied-asset-download-${assetId}`}
          onClick={onDownload}
          disabled={downloading}
          className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-2 py-0.5 text-[11px] hover:bg-bg-surface-hover"
        >
          <Download className="h-3 w-3" aria-hidden="true" />
          {downloading ? "Downloading\u2026" : "Download PDF"}
        </button>
      )}
    </span>
  );
}


function LearnerPromptBlock({ prompt }) {
  const [copied, setCopied] = useState(false);
  async function doCopy() {
    try {
      await navigator.clipboard.writeText(prompt || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_e) {
      /* no-op */
    }
  }
  return (
    <section
      data-testid="training-lesson-learner-prompt"
      className="rounded-lg border border-progress-teal/40 bg-progress-teal/5 p-5"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-fg-primary">
          <Quote className="h-4 w-4 text-progress-teal" aria-hidden="true" />
          <h2 className="text-base font-heading font-medium">
            Learner prompt to copy
          </h2>
        </div>
        <Button
          data-testid="training-lesson-learner-prompt-copy-btn"
          variant="outline"
          size="sm"
          onClick={doCopy}
          className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
        >
          {copied ? (
            <><Check className="h-3.5 w-3.5 mr-2" aria-hidden="true" />Copied</>
          ) : (
            <><Copy className="h-3.5 w-3.5 mr-2" aria-hidden="true" />Copy</>
          )}
        </Button>
      </div>
      <pre
        data-testid="training-lesson-learner-prompt-body"
        className="mt-3 text-sm text-fg-primary leading-relaxed whitespace-pre-wrap font-mono bg-bg-base/60 border border-border-subtle rounded-md p-3"
      >
        {prompt}
      </pre>
    </section>
  );
}


function KnowledgeCheckBlock({ lessonId, knowledgeCheck }) {
  const options = knowledgeCheck?.options || [];
  const [picked, setPicked] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [result, setResult] = useState(null); // {is_correct, feedback}
  const [checking, setChecking] = useState(false);
  const [checkErr, setCheckErr] = useState(null);

  const handlePick = async (label) => {
    if (checking) return;
    setPicked(label);
    setAttempts((n) => n + 1);
    setChecking(true);
    setCheckErr(null);
    setResult(null);
    try {
      const r = await api.post(
        `/training/lessons/${lessonId}/kc/check`,
        { answer: label },
      );
      setResult({
        is_correct: !!r.data?.is_correct,
        feedback:   r.data?.feedback || "",
      });
    } catch (e) {
      setCheckErr(extractApiError(e) || "Unable to check answer.");
    } finally {
      setChecking(false);
    }
  };

  const is_correct = !!result?.is_correct;
  return (
    <section
      data-testid="training-lesson-knowledge-check"
      className="rounded-lg border border-accent-warm/40 bg-accent-warm/5 p-5"
    >
      <div className="flex items-center gap-2 text-fg-primary">
        <HelpCircle className="h-4 w-4 text-accent-warm" aria-hidden="true" />
        <h2 className="text-base font-heading font-medium">
          Knowledge check
        </h2>
      </div>
      <p
        data-testid="training-lesson-knowledge-check-question"
        className="mt-2 text-sm text-fg-primary leading-relaxed"
      >
        {knowledgeCheck.question}
      </p>
      <div
        role="radiogroup"
        aria-label="Choose one"
        className="mt-3 space-y-2"
      >
        {options.map((o) => {
          const isPicked = picked === o.label;
          const resolvedCorrect =
            isPicked && result ? result.is_correct : false;
          return (
            <button
              key={o.label}
              type="button"
              role="radio"
              aria-checked={isPicked}
              data-testid={`training-lesson-knowledge-check-option-${o.label}`}
              disabled={checking}
              onClick={() => handlePick(o.label)}
              className={
                "w-full text-left flex items-start gap-3 rounded-md border px-3 py-2 text-sm transition-colors " +
                (isPicked
                  ? (resolvedCorrect
                      ? "border-progress-teal/60 bg-progress-teal/10 text-fg-primary"
                      : "border-amber-500/60 bg-amber-500/5 text-fg-primary")
                  : "border-border-subtle bg-bg-surface text-fg-primary hover:bg-bg-surface-hover")
              }
            >
              <code className="text-xs font-mono text-fg-muted mt-0.5 w-6 flex-shrink-0">
                {o.label}.
              </code>
              <span className="leading-relaxed">{o.text}</span>
            </button>
          );
        })}
      </div>
      {checking && (
        <div
          data-testid="training-lesson-knowledge-check-checking"
          className="mt-3 text-xs text-fg-muted"
        >
          Checking…
        </div>
      )}
      {checkErr && (
        <div
          data-testid="training-lesson-knowledge-check-error"
          className="mt-3 rounded-md border border-amber-500/60 bg-amber-500/5 px-3 py-2 text-sm text-fg-primary"
        >
          {checkErr}
        </div>
      )}
      {picked && result && !checking && (
        <div
          data-testid="training-lesson-knowledge-check-feedback"
          data-is-correct={is_correct ? "true" : "false"}
          className={
            "mt-3 rounded-md border px-3 py-2 text-sm leading-relaxed " +
            (is_correct
              ? "border-progress-teal/60 bg-progress-teal/5 text-fg-primary"
              : "border-amber-500/60 bg-amber-500/5 text-fg-primary")
          }
        >
          <strong className="mr-2">
            {is_correct ? "Correct." : "Try again."}
          </strong>
          {result.feedback}
          {!is_correct && (
            <div
              data-testid="training-lesson-knowledge-check-retry-note"
              className="mt-2 text-xs text-fg-muted"
            >
              Unlimited retries. Pick another option to try again
              (attempt {attempts}).
            </div>
          )}
        </div>
      )}
    </section>
  );
}


function ShortAnswerBlock({ lessonId, shortAnswer }) {
  const [text, setText] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(
          `/training/lessons/${lessonId}/short-answer`,
        );
        if (!cancelled) {
          const prev = r.data?.evidence?.short_answer_text || "";
          setText(prev);
          if (r.data?.evidence?.updated_at) {
            setSavedAt(r.data.evidence.updated_at);
          }
        }
      } catch (_e) {
        /* non-fatal; show empty textarea */
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [lessonId]);
  async function doSave() {
    setSaving(true);
    setErr(null);
    try {
      const r = await api.post(
        `/training/lessons/${lessonId}/short-answer`,
        { text },
      );
      setSavedAt(r.data?.evidence?.updated_at || new Date().toISOString());
    } catch (e) {
      setErr(extractApiError(e, "Could not save."));
    } finally {
      setSaving(false);
    }
  }
  return (
    <section
      data-testid="training-lesson-short-answer"
      className="rounded-lg border border-border-subtle bg-bg-surface p-5"
    >
      <div className="flex items-center gap-2 text-fg-primary">
        <BookOpen className="h-4 w-4" aria-hidden="true" />
        <h2 className="text-base font-heading font-medium">
          Reflect in your own words
        </h2>
      </div>
      <p
        data-testid="training-lesson-short-answer-prompt"
        className="mt-2 text-sm text-fg-primary leading-relaxed"
      >
        {shortAnswer.prompt}
      </p>
      <Textarea
        data-testid="training-lesson-short-answer-textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        disabled={!loaded || saving}
        placeholder="Type a short reflection. Only you (and administrators auditing deletion) can see this."
        className="mt-3 min-h-[120px] bg-bg-base/60"
      />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          data-testid="training-lesson-short-answer-save-btn"
          size="sm"
          variant="outline"
          onClick={doSave}
          disabled={!loaded || saving}
          className="border-border-subtle text-fg-primary hover:bg-bg-surface-hover"
        >
          <Save className="h-3.5 w-3.5 mr-2" aria-hidden="true" />
          {saving ? "Saving\u2026" : "Save my reflection"}
        </Button>
        {savedAt && !saving && (
          <span
            data-testid="training-lesson-short-answer-saved-at"
            className="text-xs text-fg-muted"
          >
            Saved {new Date(savedAt).toISOString().slice(0, 16).replace("T", " ")} UTC
          </span>
        )}
        {err && (
          <span
            data-testid="training-lesson-short-answer-error"
            className="text-xs text-destructive"
          >
            {err}
          </span>
        )}
      </div>
      <p className="mt-2 text-[11px] text-fg-muted italic">
        This reflection is stored, not scored. Nothing you type here
        is graded or sent to an AI.
      </p>
    </section>
  );
}
